<script setup lang="ts">
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import vuBtn from '@ds/vuekit/src/components/vu-btn.vue';

import useI18NAsync from '~/composables/i18nAsync';
import { ref } from 'vue';

interface Props {
  disabled?: boolean;
  dense?: boolean;
  hideTip?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  dense: false,
  hideTip: true,
});

const emit = defineEmits(['selectLocalFiles', 'dragAndDropFiles']);
const i18n = useI18NAsync('components/ordering/drag_and_drop');
const dragOver = ref(false);
</script>

<template>
  <div
    :class="[
      'drop-zone flex flex-column align-items-center justify-center grid-gap-10px pa-10px',
      {
        'drop-zone-dragged': dragOver === true,
        dense: props.dense,
        'mx-30px': !props.dense,
      },
    ]"
    @dragover.prevent="dragOver = true"
    @dragenter.prevent="dragOver = true"
    @dragleave="dragOver = false"
    @dragend="dragOver = false"
    @drop.prevent.stop="
      (e) => {
        emit('dragAndDropFiles', e);
        dragOver = false;
      }
    "
  >
    <slot name="title" />
    <div class="icon-container flex flex-column align-items-center justify-center">
      <vu-icon icon="drag-drop" />
    </div>
    <h4 class="subtitle">
      <i18n ikey="dragAndDropHere" />
    </h4>
    <span v-if="!props.hideTip" class="tip">
      <i18n ikey="upload_max50Mo" />
    </span>
    <vu-btn
      v-if="!props.dense"
      color="primary"
      class="btn-block upload-file-btn"
      @click.prevent="emit('selectLocalFiles')"
      :disabled="props.disabled"
    >
      <vu-icon icon="upload" color="white--text" />
      <i18n ikey="selectFiles" />
    </vu-btn>
    <slot v-if="!props.dense" name="tips" />
  </div>
</template>

<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

.drop-zone {
  border: #42a2da dashed 3px;
  border-radius: 20px;

  &:not(.dense) {
    background-color: #f2f6fc;
  }

  &:deep(.title) {
    font-family: '3ds';
    font-weight: 600;
  }

  &.dense {
    border-radius: 3px;
    margin-bottom: 0;

    &.isTestPart:not(.loggedIn) {
      pointer-events: none;
    }

    #drop-zone-icon {
      -webkit-user-select: none; /* Safari */
      user-select: none; /* Standard syntax */
      margin-top: 0;

      &:first-child {
        margin-bottom: 10px;
      }

      &[disabled] {
        color: variables.$grey-5;
      }
    }

    .subtitle {
      font-size: 12px;
      font-weight: normal;
      font-family: inherit;
    }

    .icon-container .fonticon.fonticon-drag-drop {
      font-size: 38px;
      height: 38px;
    }
  }

  .subtitle {
    font-family: '3ds';
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  &:deep(.fonticon-drag-drop) {
    font-size: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-items: center;
  }

  &:deep(.fonticon-drag-drop:before) {
    line-height: 1;
  }

  .upload-file-btn {
    max-width: 200px;
    height: 48px;
    font-weight: unset;
  }
}

.drop-zone-dragged {
  border-color: rgba(variables.$blue-2, 1);
  background-color: rgba(variables.$blue-0, 1);
}
</style>
